/**
 * Selector de carpeta basado en la File System Access API
 * (`window.showDirectoryPicker`).
 *
 * Por qué existe: el `<input webkitdirectory>` clásico solo entrega objetos
 * `File`. Una carpeta VACÍA no produce ningún File, así que el navegador
 * simplemente no la reporta y no hay forma de crearla al subir la estructura.
 * `showDirectoryPicker` sí enumera directorios, vacíos incluidos.
 *
 * Disponible en Chromium (Chrome/Edge); no en Firefox ni Safari. Quien lo use
 * debe consultar `supportsDirectoryPicker()` y dejar el `<input>` como
 * respaldo — allí las carpetas vacías se pierden y hay que avisarlo.
 *
 * Produce el mismo contrato que el arrastre (lib/dnd_traverse.js):
 * `{ files: [{ file, relPath }], dirs: string[], failedDirs: string[] }`,
 * donde `relPath` es la carpeta contenedora relativa e incluye la carpeta
 * superior elegida.
 *
 * Módulo puro respecto a React: el handle llega por parámetro, así que se
 * prueba con un handle falso.
 */

// Resolución de File en paralelo por tandas: uno por uno tarda minutos en
// árboles grandes; miles simultáneos saturan el runtime. Mismo criterio que
// dnd_traverse.js.
const FILE_CHUNK = 200;

/** ¿El navegador expone el selector de directorio? */
export const supportsDirectoryPicker = () =>
  typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

/**
 * Lista los hijos de un handle de directorio. Soporta `values()` (estándar) y
 * `entries()` (Chromium antiguo).
 * @returns {Promise<object[]>} handles hijos
 */
const listChildren = async (dirHandle) => {
  const out = [];
  if (typeof dirHandle.values === 'function') {
    for await (const h of dirHandle.values()) out.push(h);
    return out;
  }
  if (typeof dirHandle.entries === 'function') {
    for await (const pair of dirHandle.entries()) out.push(Array.isArray(pair) ? pair[1] : pair);
    return out;
  }
  throw new Error('El handle de directorio no expone values() ni entries().');
};

/**
 * Recorre un FileSystemDirectoryHandle y acumula archivos y carpetas.
 *
 * Un directorio ilegible se salta y se reporta en `failedDirs`, sin abortar el
 * resto del recorrido — mismo criterio que el arrastre.
 *
 * @param {object}   dirHandle
 * @param {string}   [basePath]  ruta de la carpeta contenedora
 * @param {object}   [acc]       acumulador { files, dirs, failedDirs }
 * @param {function} [onTick]    (n) => void con el conteo de archivos
 * @returns {Promise<{files: Array<{file: object, relPath: string}>, dirs: string[], failedDirs: string[]}>}
 */
export async function walkDirectoryHandle(dirHandle, basePath = '', acc = null, onTick) {
  const out = acc || { files: [], dirs: [], failedDirs: [] };
  if (!dirHandle) return out;

  const path = basePath ? `${basePath}/${dirHandle.name}` : dirHandle.name;
  // Se registra SIEMPRE, tenga contenido o no: es lo que permite crear las
  // carpetas vacías en el destino.
  out.dirs.push(path);

  let children;
  try {
    children = await listChildren(dirHandle);
  } catch (e) {
    out.failedDirs.push(path);
    return out;
  }

  const fileHandles = children.filter(h => h.kind === 'file');
  const dirHandles = children.filter(h => h.kind === 'directory');

  for (let i = 0; i < fileHandles.length; i += FILE_CHUNK) {
    const chunk = fileHandles.slice(i, i + FILE_CHUNK);
    // eslint-disable-next-line no-await-in-loop
    const resolved = await Promise.all(chunk.map(h => h.getFile().catch(() => null)));
    resolved.forEach(f => { if (f) out.files.push({ file: f, relPath: path }); });
    if (onTick) onTick(out.files.length);
  }

  // Subdirectorios en serie, para no abrir miles de lectores a la vez.
  for (const sub of dirHandles) {
    // eslint-disable-next-line no-await-in-loop
    await walkDirectoryHandle(sub, path, out, onTick);
  }

  return out;
}

/**
 * Abre el selector de carpeta del navegador y recorre lo elegido.
 * Devuelve `null` si el usuario cancela.
 *
 * @param {function} [onTick]
 * @param {function} [picker]  inyectable para pruebas; por defecto
 *                             window.showDirectoryPicker
 * @returns {Promise<{files: Array, dirs: string[], failedDirs: string[]}|null>}
 */
export async function pickDirectory(onTick, picker) {
  const show = picker || (supportsDirectoryPicker() ? window.showDirectoryPicker.bind(window) : null);
  if (!show) throw new Error('Este navegador no soporta el selector de carpeta.');
  let handle;
  try {
    handle = await show({ mode: 'read' });
  } catch (e) {
    // AbortError = el usuario cerró el diálogo. No es un fallo.
    if (e?.name === 'AbortError') return null;
    throw e;
  }
  return walkDirectoryHandle(handle, '', null, onTick);
}
